import { Link } from 'react-router-dom';

export default function HowItWorksPage() {
  return (
    <div>
      {/* Hero */}
      <section style={{ background: 'var(--hero-gradient)', color: '#fff', padding: '96px 0 80px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-30%', right: '-10%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,70,239,0.35) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-40%', left: '-10%', width: 520, height: 520, borderRadius: '50%', background: 'radial-gradient(circle, rgba(249,115,22,0.3) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="container-narrow" style={{ position: 'relative' }}>
          <span className="chip" style={{ marginBottom: 20 }}>✨ It's easier than you think</span>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 60px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 18 }}>
            How <span className="gradient-text">PerkVora</span> works
          </h1>
          <p style={{ fontSize: 18, opacity: 0.86, maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
            From finding the perfect deal to cashing out your rewards — here's exactly how you'll save more on everything you already buy.
          </p>
        </div>
      </section>

      {/* 4-Step Flow */}
      <section className="section" style={{ background: '#fff' }}>
        <div className="container">
          <BigStep num="01" title="Discover deals that match you" desc="Browse 10,000+ verified coupons, cashback offers, and exclusive deals across 500+ brands. Search by category, location, or just let our personalized feed find the ones for you." visual="🔍" />
          <BigStep num="02" title="Claim in one click" desc="No print-outs, no clipping, no 'sign up for our newsletter to unlock'. Grab the code, activate cashback, or open the exclusive link straight to the merchant. Takes 2 seconds." visual="⚡" reverse />
          <BigStep num="03" title="Check out and save" desc="Paste the code at checkout or shop through our activation link. Save instantly on the purchase price — and earn cashback automatically on qualifying orders." visual="💳" />
          <BigStep num="04" title="Cash out to your bank" desc="Watch your PerkVora wallet grow. Cash out to your bank via ACH or debit card — from just $5. No expiration tricks, no hidden fees." visual="💰" reverse id="cashback" />
        </div>
      </section>

      {/* Types of perks */}
      <section className="section section-tinted">
        <div className="container">
          <div className="text-center" style={{ marginBottom: 56 }}>
            <span className="eyebrow">Every kind of perk</span>
            <h2 className="section-h2" style={{ marginTop: 12 }}>Five ways to save on PerkVora</h2>
            <p className="section-lead">Different deals for different moments — all under one roof.</p>
          </div>
          <div className="grid-3">
            <PerkType icon="🏷️" title="Promo Codes" desc="Copy a code, paste at checkout, save instantly. Classic — but ours actually work (we verify daily)." />
            <PerkType icon="💰" title="Cashback" desc="Shop through our activation link and earn a % back on your order. Stacks with coupons." />
            <PerkType icon="🎁" title="Freebies & BOGO" desc="Free gifts with purchase, buy-one-get-ones, and exclusive bonuses — curated and tested." />
            <PerkType icon="✨" title="Exclusive Perks" desc="Deals you won't find anywhere else, negotiated directly with merchants for PerkVora members." />
            <PerkType icon="📍" title="In-Store Deals" desc="Show the offer at checkout to save at local spots — no app-switching, no printing." />
            <PerkType icon="🎉" title="Flash Drops" desc="Limited-time deals that drop throughout the day. Set alerts to never miss one." />
          </div>
        </div>
      </section>

      {/* Loyalty tiers */}
      <section className="section" style={{ background: '#fff' }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: 56 }}>
            <span className="eyebrow">Loyalty that rewards loyalty</span>
            <h2 className="section-h2" style={{ marginTop: 12 }}>The more you save, the more you earn</h2>
            <p className="section-lead">Every redemption earns PerkVora Points and moves you up the tier ladder. Higher tiers = more cashback, early access, and premium perks.</p>
          </div>
          <div className="grid-3">
            <TierCard name="Silver" tagline="Welcome aboard" perks={['Up to 5% cashback', 'Earn 10 points per redemption', 'Weekly deals newsletter', 'Standard payout speed']} />
            <TierCard name="Gold" tagline="Top-tier saver" featured perks={['Up to 12% cashback', 'Bonus points events', 'Early access to flash drops', 'Priority support', 'Exclusive Gold-only deals']} />
            <TierCard name="Platinum" tagline="VIP treatment" perks={['Up to 25% cashback', 'Double points every week', 'Exclusive partner events', 'Dedicated account manager', 'Birthday cashback bonus']} />
          </div>
        </div>
      </section>

      {/* FAQ teaser */}
      <section className="section section-tinted">
        <div className="container-narrow text-center">
          <h2 className="section-h2">Got questions?</h2>
          <p className="section-lead">We've got answers. Dive into the FAQ or reach out — we respond to every message.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/faq" className="btn btn-primary btn-lg">Browse FAQ</Link>
            <Link to="/contact" className="btn btn-outline btn-lg">Contact us</Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section">
        <div className="container">
          <div className="cta-banner">
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 16, lineHeight: 1.1 }}>
              Ready to save smarter?
            </h2>
            <p style={{ fontSize: 16, maxWidth: 520, margin: '0 auto 32px', opacity: 0.95 }}>Create your free account in under 60 seconds. No credit card required, ever.</p>
            <Link to="/register" className="btn btn-xl" style={{ background: '#fff', color: '#1A1B4B', fontWeight: 700 }}>
              Get started free →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function BigStep({ num, title, desc, visual, reverse, id }) {
  return (
    <div id={id} style={{ display: 'grid', gridTemplateColumns: reverse ? '1fr 1fr' : '1fr 1fr', gap: 64, alignItems: 'center', marginBottom: 96 }} className="step-grid">
      <div style={{ order: reverse ? 2 : 1 }}>
        <p style={{ fontSize: 72, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.04em', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }} className="gradient-text">{num}</p>
        <h3 style={{ fontSize: 'clamp(26px, 3vw, 36px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '18px 0 16px', lineHeight: 1.2 }}>{title}</h3>
        <p style={{ fontSize: 16, color: '#4A5568', lineHeight: 1.7 }}>{desc}</p>
      </div>
      <div style={{ order: reverse ? 1 : 2, background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)', borderRadius: 24, padding: 64, textAlign: 'center', minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 120 }}>{visual}</div>
      </div>
      <style>{`@media(max-width:880px){.step-grid{grid-template-columns:1fr!important;gap:32px!important;margin-bottom:56px!important}.step-grid>*{order:unset!important}}`}</style>
    </div>
  );
}

function PerkType({ icon, title, desc }) {
  return (
    <div className="feature-tile">
      <div className="feature-icon">{icon}</div>
      <h3 className="feature-title">{title}</h3>
      <p className="feature-desc">{desc}</p>
    </div>
  );
}

function TierCard({ name, tagline, perks, featured }) {
  return (
    <div className={featured ? 'plan-card plan-featured' : 'plan-card'}>
      {featured && <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'var(--brand-gradient)', color: '#fff', padding: '4px 14px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Most popular</div>}
      <p style={{ fontSize: 11, fontWeight: 700, color: '#8B94A8', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 6 }}>{tagline}</p>
      <h3 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 20 }} className={featured ? 'gradient-text' : ''}>{name}</h3>
      <div style={{ borderTop: '1px solid #E4E7F0', paddingTop: 18 }}>
        {perks.map(p => <div key={p} className="plan-feature">{p}</div>)}
      </div>
    </div>
  );
}
